// Journey Lab's render-first live oversight panel. All reads, polling and one-shot mutations belong to
// useJourneyLive; these components render the safe projection and own only transient interaction state.

import { useState } from 'react';
import type {
  JourneyLiveArmMode,
  JourneyLiveControlView,
  JourneyLivePreflightCheckView,
  JourneyLivePreflightView,
  JourneyLiveStatusView,
} from '../journey-live-api.ts';
import { journeyStamp } from '../journey-lab-view.ts';
import {
  journeyLiveControlLabel,
  journeyLiveControlTone,
  journeyLiveModeLabel,
  journeyLiveModeProofBoundary,
} from '../journey-live-view.ts';
import type { JourneyLiveController } from '../useJourneyLive.ts';
import { Panel, StatusPill } from '../primitives.tsx';
import { JourneyLiveControlActions } from './JourneyLiveControlActions.tsx';
import { JourneyLiveBudgetCaps, JourneyLiveInputReadiness } from './JourneyLiveReadiness.tsx';

const LIVE_MODES = ['no_egress_rehearsal', 'provider_live'] as const;

const MODE_OPTIONS: readonly {
  readonly mode: JourneyLiveArmMode;
  readonly label: string;
  readonly detail: string;
}[] = LIVE_MODES.map((mode) => ({
  mode,
  label: journeyLiveModeLabel(mode),
  detail: journeyLiveModeProofBoundary(mode),
}));

function BlockerList({ blockers }: { readonly blockers: readonly string[] }): React.JSX.Element {
  if (blockers.length === 0) {
    return <p className="text-sm text-text-muted">No configuration blockers reported.</p>;
  }
  return (
    <ul aria-label="Full-flow configuration blockers" className="space-y-2">
      {blockers.map((blocker, index) => (
        <li
          key={`${blocker}-${String(index)}`}
          className="border-l-2 border-clay pl-3 text-sm text-text-muted"
        >
          {blocker}
        </li>
      ))}
    </ul>
  );
}

function ModeOption({
  option,
  selected,
  disabled,
  unavailable,
  onSelect,
}: {
  readonly option: (typeof MODE_OPTIONS)[number];
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly unavailable: boolean;
  readonly onSelect: () => void;
}): React.JSX.Element {
  return (
    <label
      className={`flex items-start gap-3 rounded-sm border p-3 ${
        selected ? 'border-forest bg-surface-sunken' : 'border-border'
      }${unavailable ? ' opacity-60' : ''}`}
    >
      <input
        type="radio"
        name="journey-live-mode"
        value={option.mode}
        checked={selected}
        disabled={disabled || unavailable}
        onChange={onSelect}
      />
      <span>
        <span className="block text-sm font-medium text-text">{option.label}</span>
        <span className="mt-1 block text-xs leading-relaxed text-text-muted">{option.detail}</span>
        {unavailable ? (
          <span className="mt-1 block text-xs text-clay">Configuration is not ready.</span>
        ) : null}
      </span>
    </label>
  );
}

function ModePicker({
  selected,
  disabled,
  providerLiveReady,
  onSelect,
}: {
  readonly selected: JourneyLiveArmMode;
  readonly disabled: boolean;
  readonly providerLiveReady: boolean;
  readonly onSelect: (mode: JourneyLiveArmMode) => void;
}): React.JSX.Element {
  return (
    <fieldset className="mt-5" disabled={disabled}>
      <legend className="text-sm font-semibold text-text">Full-flow mode</legend>
      <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
        {MODE_OPTIONS.map((option) => (
          <ModeOption
            key={option.mode}
            option={option}
            selected={selected === option.mode}
            disabled={disabled}
            unavailable={option.mode === 'provider_live' && !providerLiveReady}
            onSelect={() => onSelect(option.mode)}
          />
        ))}
      </div>
    </fieldset>
  );
}

function PreflightCheck({
  check,
}: {
  readonly check: JourneyLivePreflightCheckView;
}): React.JSX.Element {
  return (
    <li
      className="flex items-start justify-between gap-4 border-b border-border pb-2 text-sm last:border-b-0"
      data-preflight-check={check.code}
      data-preflight-check-pass={String(check.pass)}
    >
      <span>
        <span className="ritona-numeric block text-xs text-text-subtle">{check.code}</span>
        <span className="mt-1 block text-text-muted">{check.detail}</span>
      </span>
      <span className={check.pass ? 'text-forest' : 'text-clay'}>
        {check.pass ? 'Passed' : 'Blocked before completion'}
      </span>
    </li>
  );
}

function PreflightEvidence({
  preflight,
}: {
  readonly preflight: JourneyLivePreflightView;
}): React.JSX.Element {
  return (
    <section
      aria-label="Latest full-flow preflight"
      className="mt-5 border-t border-border pt-5"
      data-preflight-result={preflight.pass ? 'pass' : 'fail'}
      data-preflight-mode={preflight.mode}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-text">Latest preflight</h3>
          <p className="mt-1 text-xs text-text-subtle">
            {journeyLiveModeLabel(preflight.mode)} · checked{' '}
            <time dateTime={preflight.checkedAt}>{journeyStamp(preflight.checkedAt)}</time>
          </p>
        </div>
        <StatusPill tone={preflight.pass ? 'go' : 'blocked'}>
          {preflight.pass ? 'Passed' : 'Blocked before completion'}
        </StatusPill>
      </div>
      <ul aria-label="Full-flow preflight checks" className="mt-4 space-y-2">
        {preflight.checks.map((check) => (
          <PreflightCheck key={check.code} check={check} />
        ))}
      </ul>
    </section>
  );
}

function StatusFacts({ status }: { readonly status: JourneyLiveStatusView }): React.JSX.Element {
  const facts: readonly {
    readonly label: string;
    readonly value: string;
    readonly numeric?: boolean;
  }[] = [
    { label: 'Required model', value: `OpenAI · ${status.requiredModel.modelId}` },
    {
      label: 'Configuration',
      value: status.configured ? 'Reviewed manifest loaded' : 'Not configured',
    },
    {
      label: 'Provider-backed test readiness',
      value: status.providerLiveReady ? 'Ready for preflight' : 'Blocked',
    },
    { label: 'Control generation', value: String(status.control.generation), numeric: true },
  ];
  return (
    <dl className="mt-5 grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
      {facts.map((fact) => (
        <div key={fact.label}>
          <dt className="text-xs text-text-subtle">{fact.label}</dt>
          <dd className={`${fact.numeric === true ? 'ritona-numeric ' : ''}mt-1 text-text-muted`}>
            {fact.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function StatusProjection({
  live,
  status,
  mode,
  onMode,
}: {
  readonly live: JourneyLiveController;
  readonly status: JourneyLiveStatusView;
  readonly mode: JourneyLiveArmMode;
  readonly onMode: (mode: JourneyLiveArmMode) => void;
}): React.JSX.Element {
  return (
    <>
      <StatusFacts status={status} />
      <div className="mt-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <JourneyLiveBudgetCaps budgets={status.budgets} />
        <JourneyLiveInputReadiness readiness={status.readiness} />
      </div>
      <div className="mt-5 border-t border-border pt-5">
        <h3 className="mb-2 text-sm font-semibold text-text">Configuration blockers</h3>
        <BlockerList blockers={status.blockers} />
      </div>
      <ModePicker
        selected={mode}
        disabled={status.control.state === 'armed' || live.operation !== null}
        providerLiveReady={status.providerLiveReady}
        onSelect={onMode}
      />
      {status.latestPreflight === undefined ? (
        <p className="mt-5 border-t border-border pt-5 text-sm text-text-subtle">
          No full-flow preflight has been recorded yet.
        </p>
      ) : (
        <PreflightEvidence preflight={status.latestPreflight} />
      )}
      <p className="mt-4 text-xs text-text-subtle" aria-live="polite">
        {status.control.reason}
      </p>
    </>
  );
}

function LiveControlHeader({
  control,
}: {
  readonly control: JourneyLiveControlView | undefined;
}): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div className="ritona-numeric text-xs uppercase tracking-wide text-forest">
          Controlled scenario testing
        </div>
        <h2 id="journey-live-control-title" className="mt-1 text-base font-semibold text-text">
          Full-flow test control
        </h2>
        <p className="mt-1 max-w-3xl text-sm text-text-muted">
          Choose a rehearsal with no external actions or a provider-backed test using the reviewed
          bindings. Both modes remain guarded by the same durable kill switch.
        </p>
      </div>
      {control === undefined ? (
        <StatusPill tone="neutral">Unavailable</StatusPill>
      ) : (
        <StatusPill tone={journeyLiveControlTone(control.state)}>
          {journeyLiveControlLabel(control.state)}
        </StatusPill>
      )}
    </div>
  );
}

function LiveStatusFallback({ loading }: { readonly loading: boolean }): React.JSX.Element {
  return (
    <p aria-busy={loading} className="mt-4 text-sm text-text-subtle">
      {loading ? 'Loading full-flow control status…' : 'No full-flow control status is available.'}
    </p>
  );
}

export function JourneyLiveControl({
  live,
}: {
  readonly live: JourneyLiveController;
}): React.JSX.Element {
  const [selectedMode, setSelectedMode] = useState<JourneyLiveArmMode>('no_egress_rehearsal');
  const [confirmationFor, setConfirmationFor] = useState<string | null>(null);
  const status = live.status;
  const control = status?.control;
  const mode =
    control?.state === 'armed' && control.armMode !== undefined ? control.armMode : selectedMode;
  const onMode = (next: JourneyLiveArmMode): void => {
    setSelectedMode(next);
    setConfirmationFor(null);
  };
  return (
    <Panel
      className="p-5"
      dataMarker={{
        name: 'live-availability',
        value: status?.configured === true ? 'available' : 'unavailable',
      }}
    >
      <section
        aria-labelledby="journey-live-control-title"
        data-live-control-state={control?.state}
      >
        <LiveControlHeader control={control} />
        {live.error !== null ? (
          <p role="alert" className="mt-4 border-l-2 border-clay pl-3 text-sm text-text-muted">
            {live.error}
          </p>
        ) : null}
        {status === null ? (
          <LiveStatusFallback loading={live.loading} />
        ) : (
          <StatusProjection live={live} status={status} mode={mode} onMode={onMode} />
        )}
        <JourneyLiveControlActions
          live={live}
          selectedMode={mode}
          confirmationFor={confirmationFor}
          setConfirmationFor={setConfirmationFor}
        />
      </section>
    </Panel>
  );
}
